import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../hooks/useRole';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import toast from 'react-hot-toast';
import * as Lu from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import { format, addDays, startOfDay, parseISO } from 'date-fns';
import { cn } from '../lib/utils';

const SECTION = ({ title, icon, iconBg, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden"
  >
    <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-50 dark:border-slate-800 bg-slate-50/50">
      <div className={`p-2 rounded-xl ${iconBg}`}>
        <SafeIcon icon={icon} size={16} className="text-white" />
      </div>
      <h3 className="text-xs font-black uppercase tracking-widest text-slate-700 dark:text-white">{title}</h3>
    </div>
    <div className="p-6">{children}</div>
  </motion.section>
);

export default function Settings() {
  const { user } = useAuth();
  const { profile, loading: roleLoading } = useRole();
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [profileExists, setProfileExists] = useState(true);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    mobile_number: '',
    email: '',
    teacher_nickname: '',
  });

  const [passwords, setPasswords] = useState({ new: '', confirm: '' });

  const [reminders, setReminders] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(true);
  const [wapiPhone, setWapiPhone] = useState(localStorage.getItem('wa_phone_id') || '');
  const [wapiToken, setWapiToken] = useState(localStorage.getItem('wa_token') || '');
  const [apiConfigOpen, setApiConfigOpen] = useState(false);
  const [sending, setSending] = useState({});

  useEffect(() => {
    if (profile) {
      setProfileExists(true);
      setForm({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        mobile_number: profile.mobile_number || '',
        email: profile.email || user?.email || '',
        teacher_nickname: profile.teacher_nickname || '',
      });
    } else if (!roleLoading && user) {
      setProfileExists(false);
      setForm(p => ({ ...p, email: user?.email || '' }));
    }
  }, [profile, user, roleLoading]);

  useEffect(() => { fetchReminders(); }, []);

  const fetchReminders = async () => {
    setLoadingReminders(true);
    try {
      const { data: enrollments } = await supabase
        .from('student_subjects')
        .select('*,students(id,full_name),subjects(id,name,level)')
        .not('scheduled_days', 'is', null);

      if (!enrollments) return;

      const today = startOfDay(new Date());
      const targetDate = addDays(today, 3);
      const targetDayName = format(targetDate, 'EEE');
      const upcoming = [];

      for (const en of enrollments) {
        const scheduledDays = en.scheduled_days || [];
        if (!scheduledDays.includes(targetDayName)) continue;
        if (en.enrollment_start_date) {
          const start = startOfDay(parseISO(en.enrollment_start_date));
          if (targetDate < start) continue;
        }
        if (en.enrollment_end_date) {
          const end = startOfDay(parseISO(en.enrollment_end_date));
          if (targetDate > end) continue;
        }
        const { data: guardians } = await supabase
          .from('guardians').select('*').eq('student_id', en.student_id);
        upcoming.push({
          enrollment: en, student: en.students, subject: en.subjects,
          targetDate, guardians: guardians || [],
        });
      }
      setReminders(upcoming);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingReminders(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!profileExists) {
        const { error } = await supabase.from('profiles').insert([{
          id: user.id,
          email: form.email || user.email,
          first_name: form.first_name,
          last_name: form.last_name,
          mobile_number: form.mobile_number,
          teacher_nickname: form.teacher_nickname,
          role: 'teacher',
        }]);
        if (error) throw error;
        setProfileExists(true);
        toast.success('Profile created!');
      } else {
        const { error } = await supabase
          .from('profiles')
          .update({
            first_name: form.first_name,
            last_name: form.last_name,
            mobile_number: form.mobile_number,
            teacher_nickname: form.teacher_nickname,
          })
          .eq('id', user.id);
        if (error) throw error;

        if (form.email && form.email !== user.email) {
          const { error: authErr } = await supabase.auth.updateUser({ email: form.email });
          if (authErr) throw authErr;
          toast.success('Email update sent — check your inbox to confirm');
        } else {
          toast.success('Profile updated!');
        }
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // When password is changed successfully, clear the default password flag
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) return toast.error('Passwords do not match');
    if (passwords.new.length < 6) return toast.error('Password must be at least 6 characters');
    setPwSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: passwords.new });
      if (error) throw error;

      // Clear the default password flag if it was set
      if (profile?.is_default_password) {
        await supabase.from('profiles')
          .update({ is_default_password: false })
          .eq('id', user.id);
      }

      toast.success('Password changed successfully!');
      setPasswords({ new: '', confirm: '' });
      // Reload the page so the banner disappears
      window.location.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPwSaving(false);
    }
  };

  const saveWaConfig = () => {
    localStorage.setItem('wa_phone_id', wapiPhone);
    localStorage.setItem('wa_token', wapiToken);
    toast.success('WhatsApp config saved locally');
    setApiConfigOpen(false);
  };

  const buildMessage = (reminder, guardianName) => {
    const teacherName = profile?.teacher_nickname ||
      `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() ||
      'Your Teacher';
    const dateStr = format(reminder.targetDate, 'EEEE, dd MMM yyyy');
    return `Hi ${guardianName || 'Parent'}! 👋\n\nThis is a friendly reminder from *${teacherName}* that *${reminder.student?.full_name}* has a *${reminder.subject?.name}* class on *${dateStr}*.\n\nPlease ensure your child is prepared and on time. 📚\n\nThank you!`;
  };

  const sendViaApi = async (reminder, guardian) => {
    const token = localStorage.getItem('wa_token');
    const phoneId = localStorage.getItem('wa_phone_id');
    if (!token || !phoneId) { toast.error('Configure WhatsApp API first'); setApiConfigOpen(true); return; }
    const phone = guardian.phone?.replace(/\D/g, '');
    if (!phone) return toast.error('No phone number for this guardian');
    const key = `${reminder.student?.id}-${guardian.id}`;
    setSending(p => ({ ...p, [key]: true }));
    try {
      const res = await fetch(`https://graph.facebook.com/v19.0/${phoneId}/messages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messaging_product: 'whatsapp', to: phone, type: 'text',
          text: { body: buildMessage(reminder, guardian.name) },
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message);
      toast.success(`Sent to ${guardian.name || phone}!`);
    } catch (err) {
      toast.error(`API Error: ${err.message}`);
    } finally {
      setSending(p => ({ ...p, [key]: false }));
    }
  };

  const sendViaLink = (reminder, guardian) => {
    const phone = guardian.phone?.replace(/\D/g, '');
    if (!phone) return toast.error('No phone number');
    const msg = encodeURIComponent(buildMessage(reminder, guardian.name));
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  const hasApiConfig = localStorage.getItem('wa_token') && localStorage.getItem('wa_phone_id');

  if (roleLoading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );

  const displayName = profile?.teacher_nickname ||
    `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() ||
    user?.email;

  return (
    <div className="max-w-5xl mx-auto space-y-5 pb-24 px-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-tighter dark:text-white">Settings</h1>
          <p className="text-slate-400 text-xs font-bold uppercase mt-0.5">Account & Notifications</p>
        </div>
        <div className="bg-primary-600 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-primary-500/20">
          {profile?.role || 'teacher'}
        </div>
      </div>

      {/* Default Password Warning Banner (inline in settings) */}
      {profile?.is_default_password && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 flex items-start gap-3"
        >
          <SafeIcon icon={Lu.LuShieldAlert} size={20} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-black uppercase text-amber-700 tracking-wide">
              Action Required: Change Your Password
            </p>
            <p className="text-xs text-amber-600 mt-1 leading-relaxed">
              You are currently using the default password <code className="bg-amber-100 px-1 rounded font-mono">password123</code>.
              Please update it below to secure your account.
            </p>
          </div>
        </motion.div>
      )}

      {/* Profile Card */}
      <div className="bg-gradient-to-r from-primary-600 to-indigo-600 rounded-2xl p-6 text-white shadow-xl shadow-primary-500/20">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-white/20 border-2 border-white/30 flex items-center justify-center text-2xl font-black">
            {displayName?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <p className="text-lg font-black tracking-tight">{displayName}</p>
            <p className="text-white/70 text-xs font-bold uppercase">{user?.email}</p>
            {!profileExists && (
              <span className="mt-1 inline-flex items-center gap-1 bg-amber-400/30 px-2 py-0.5 rounded-full text-[9px] font-black uppercase text-amber-200">
                <SafeIcon icon={Lu.LuAlertCircle} size={9} /> Profile not set up yet
              </span>
            )}
            {profile?.teacher_nickname && (
              <span className="mt-1 inline-flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded-full text-[9px] font-black uppercase">
                <SafeIcon icon={Lu.LuStar} size={9} /> Nickname Active
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Personal Details */}
        <div className="space-y-5">
          <SECTION title="Personal Details" icon={Lu.LuUser} iconBg="bg-primary-600">
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Input label="First Name" value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} placeholder="Jane" />
                <Input label="Last Name" value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} placeholder="Smith" />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Teacher Nickname</label>
                <input
                  value={form.teacher_nickname}
                  onChange={e => setForm(p => ({ ...p, teacher_nickname: e.target.value }))}
                  placeholder="Ms. Jane (used in notifications & display)"
                  className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wide px-1">
                  Used in WhatsApp reminders & shown instead of real name
                </p>
              </div>
              <Input label="Mobile Number" value={form.mobile_number} onChange={e => setForm(p => ({ ...p, mobile_number: e.target.value }))} placeholder="+60 12-345 6789" />
              <Input label="Email Address" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="jane@happynestling.com" />
              <Button type="submit" className="w-full py-3 font-black uppercase text-[10px] tracking-widest" disabled={saving}>
                {saving ? 'Saving...' : profileExists ? 'Save Personal Details' : 'Create Profile'}
              </Button>
            </form>
          </SECTION>

          <SECTION title="Change Password" icon={Lu.LuLock} iconBg="bg-rose-500">
            <form onSubmit={handleChangePassword} className="space-y-4">
              {profile?.is_default_password && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
                  <SafeIcon icon={Lu.LuShieldAlert} size={14} className="text-amber-600 shrink-0" />
                  <p className="text-[9px] font-black uppercase text-amber-700">
                    Change your default password now!
                  </p>
                </div>
              )}
              <Input label="New Password" type="password" value={passwords.new} onChange={e => setPasswords(p => ({ ...p, new: e.target.value }))} placeholder="Min. 6 characters" />
              <Input label="Confirm New Password" type="password" value={passwords.confirm} onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))} placeholder="Repeat password" />
              {passwords.new && passwords.confirm && passwords.new !== passwords.confirm && (
                <p className="text-[10px] font-bold text-rose-500 uppercase px-1">Passwords do not match</p>
              )}
              <Button
                type="submit"
                variant={profile?.is_default_password ? 'primary' : 'secondary'}
                className="w-full py-3 font-black uppercase text-[10px] tracking-widest border-2"
                disabled={pwSaving || !passwords.new || !passwords.confirm}
              >
                {pwSaving ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </SECTION>
        </div>

        {/* WhatsApp Reminders */}
        <div className="space-y-5">
          <SECTION title="WhatsApp Class Reminders" icon={Lu.LuMessageCircle} iconBg="bg-emerald-500">
            <div className="space-y-4">
              <div className={cn('rounded-xl p-4 border flex items-start gap-3', hasApiConfig ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100')}>
                <SafeIcon icon={hasApiConfig ? Lu.LuCheckCircle : Lu.LuAlertCircle} size={16} className={hasApiConfig ? 'text-emerald-600 shrink-0 mt-0.5' : 'text-amber-600 shrink-0 mt-0.5'} />
                <div className="flex-1 min-w-0">
                  <p className={cn('text-[10px] font-black uppercase tracking-widest', hasApiConfig ? 'text-emerald-700' : 'text-amber-700')}>
                    {hasApiConfig ? 'WhatsApp API Connected' : 'WhatsApp API Not Configured'}
                  </p>
                  <p className="text-[9px] text-slate-500 font-medium mt-0.5">
                    {hasApiConfig ? 'Messages will be sent automatically via Meta Business API.' : 'Configure API to send automatically, or use the WA button to send manually.'}
                  </p>
                </div>
                <button onClick={() => setApiConfigOpen(true)} className="text-[9px] font-black uppercase text-primary-600 hover:underline shrink-0">
                  {hasApiConfig ? 'Edit' : 'Setup'}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-black uppercase text-slate-600 dark:text-white">Classes in 3 Days</p>
                  <p className="text-[9px] text-slate-400 font-bold uppercase">{format(addDays(new Date(), 3), 'EEEE, dd MMM yyyy')}</p>
                </div>
                <button onClick={fetchReminders} className="p-2 bg-slate-50 rounded-lg text-slate-400 hover:text-primary-600 border">
                  <SafeIcon icon={Lu.LuRefreshCw} size={14} />
                </button>
              </div>

              {loadingReminders ? (
                <div className="space-y-2">{[1, 2].map(i => <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-xl" />)}</div>
              ) : reminders.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <SafeIcon icon={Lu.LuCalendarCheck} size={22} className="text-emerald-500" />
                  </div>
                  <p className="text-[10px] font-black uppercase text-slate-400">No classes scheduled in 3 days</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                  {reminders.map((reminder, i) => (
                    <div key={i} className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-black text-xs uppercase dark:text-white">{reminder.student?.full_name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <Badge variant="primary" className="text-[7px] uppercase">{reminder.subject?.name}</Badge>
                            <Badge variant="default" className="text-[7px] uppercase">{reminder.subject?.level}</Badge>
                          </div>
                        </div>
                        <p className="text-[9px] font-black text-emerald-600 uppercase shrink-0">{format(reminder.targetDate, 'EEE, dd MMM')}</p>
                      </div>
                      {reminder.guardians.length === 0 ? (
                        <p className="text-[9px] text-slate-400 font-bold uppercase">No guardian contacts added</p>
                      ) : (
                        <div className="space-y-2">
                          {reminder.guardians.map((g, gi) => (
                            <div key={gi} className="flex items-center justify-between gap-2 bg-white dark:bg-slate-800 rounded-lg p-2.5 border">
                              <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase truncate dark:text-white">{g.name || 'Unnamed'}</p>
                                <p className="text-[9px] text-slate-400 font-bold">{g.phone} · {g.label}</p>
                              </div>
                              <div className="flex gap-1.5 shrink-0">
                                <button onClick={() => sendViaApi(reminder, g)} disabled={sending[`${reminder.student?.id}-${g.id}`]} className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 text-white text-[9px] font-black uppercase rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                                  {sending[`${reminder.student?.id}-${g.id}`] ? <SafeIcon icon={Lu.LuLoader} size={11} className="animate-spin" /> : <SafeIcon icon={Lu.LuSend} size={11} />} API
                                </button>
                                <button onClick={() => sendViaLink(reminder, g)} className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-100 text-slate-700 text-[9px] font-black uppercase rounded-lg hover:bg-slate-200 transition-colors border">
                                  <SafeIcon icon={Lu.LuExternalLink} size={11} /> WA
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {reminders.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                  <p className="text-[9px] font-black uppercase text-emerald-700 tracking-widest mb-2">Message Preview</p>
                  <div className="bg-white rounded-lg p-3 border border-emerald-100">
                    <p className="text-[10px] text-slate-600 leading-relaxed whitespace-pre-line">
                      {buildMessage(reminders[0], reminders[0].guardians[0]?.name || 'Parent')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </SECTION>
        </div>
      </div>

      {/* WhatsApp API Config Modal */}
      <Modal isOpen={apiConfigOpen} onClose={() => setApiConfigOpen(false)} title="WhatsApp Business API Setup" className="max-w-lg">
        <div className="p-5 space-y-5">
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <p className="text-[10px] font-black uppercase text-blue-700 tracking-widest mb-1">Meta Cloud API Required</p>
            <p className="text-[10px] text-slate-600 leading-relaxed">
              You need a Meta Business account with WhatsApp Cloud API access. Get your credentials from{' '}
              <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-bold">developers.facebook.com</a>.
            </p>
          </div>
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1 block">Phone Number ID</label>
              <input value={wapiPhone} onChange={e => setWapiPhone(e.target.value)} placeholder="1234567890123456" className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1 block">Access Token (Permanent)</label>
              <input value={wapiToken} onChange={e => setWapiToken(e.target.value)} placeholder="EAAxxxxxx..." type="password" className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-start gap-2">
            <SafeIcon icon={Lu.LuInfo} size={13} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[9px] text-amber-700 font-bold leading-relaxed">
              Credentials are stored locally in your browser only.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setApiConfigOpen(false)} className="flex-1">Cancel</Button>
            <Button onClick={saveWaConfig} className="flex-[2] font-black uppercase text-[10px]">Save Configuration</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}